import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';

import Divider from '@mui/material/Divider';
import {
  ApiCustomField,
  ApiEnumGroup,
  ApiIdentityOut,
  ApiItemData,
  ApiOrganizationProfile,
  ApiUserInfo
} from '@agunity/api-v4/build/data-contracts';
import { ApiColumnType, ApiItemTypes, ApiRoleForUser } from '@agunity/api-v4';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getStructure } from '../../../redux/slices/organizations';
import {
  setFunctionStructureList,
  setSelectedRoles
} from '../../../redux/slices/staff';

import AgIconButton from '../../../stories/AgIconButton';
import AgTypography from '../../../stories/Styles/AgTypography';
import AgOrganizationalChips from '../../../stories/Chips/AgOrganizationalChips';
import { AgColor } from '../../../stories/Styles/AgColor';
import AgRoleChip from '../../../stories/Chips/AgRoleChips';
import AgButton from '../../../stories/Components/AgButton';
import AgModal from '../../../stories/AgModal';
import AgCheckBox from '../../../stories/Components/AgCheckBox';
import AgTextField from '../../../stories/Components/AgTextField';
import AgSimpleTable from '../../../stories/Components/AgSimpleTable';

import { HasOnBlurProps } from '../AddStaff/AddStaff';
import { CustomFieldGroupProps } from './EditStaffDetails';

import {
  CustomFieldColumnType,
  MANDATORY_LEVEL,
  MEMBER_PARENT_FIELD_TYPE_NUMBER,
  ROOT_PATHS
} from '../../../Constants';
import edit_square from '../../../../src/stories/assets/edit_square.svg';
import { Box } from '@mui/material';

export interface ProfileDetailsProps {
  key: number;
  editAccess: boolean;
  data?: ApiUserInfo;
  // eslint-disable-next-line no-unused-vars
  setOrgData?: (data: ApiItemData[]) => void;
  // eslint-disable-next-line no-unused-vars
  setOrgDataClientId?: (clientId: string) => void;
  identity?: ApiIdentityOut;
  orgData?: ApiOrganizationProfile;
  tagList?: ApiCustomField[] | null;
  setHasStaffProfileError?: Dispatch<SetStateAction<boolean>>;
  showStaffProfileError?: boolean;
  enumList?: ApiEnumGroup[];
  hasOnBlur?: HasOnBlurProps;
  setHasOnBlur?: Dispatch<SetStateAction<HasOnBlurProps>>;
  isProfile?: boolean;
}

const Index = ({
  editAccess,
  data,
  setOrgDataClientId,
  orgData,
  tagList,
  setHasStaffProfileError,
  enumList,
  isProfile
}: ProfileDetailsProps) => {
  const messages = useIntl().formatMessage;
  const dispatch = useAppDispatch();
  const functionStructureList = useAppSelector(
    (state) => state.staff.functionStructureList
  );
  const allRoles = useAppSelector((state) => state.staff.allRolesUnderOrg);
  const [, setTypeName] = useState<string>('');
  const [customFieldGroupData, setCustomFieldGroupData] = useState<
    CustomFieldGroupProps[]
  >([]);
  const [openRoleSelectionModal, setOpenRoleSelectionModal] =
    useState<boolean>(false);
  const [selectedRolesTemp, setSelectedRolesTemp] = useState<ApiRoleForUser[]>(data?.roles ?? []);

  const handleEditRoles = () => {
    return (
      <AgIconButton
        icon={
          <img
            src={edit_square}
            alt='AgUnity'
            style={{ width: '16px', height: '16px' }}
          />
        }
        buttonStyle={{ width: '40px', height: '36px', padding: 0 }}
        onClick={() => {
          setOpenRoleSelectionModal(true);
        }}
      />
    );
  };

  const handleRoleSelection = (role: ApiRoleForUser) => {
    const roleId = role.id;
    if (selectedRolesTemp.some((selectedRole) => selectedRole.id === roleId)) {
      const updatedRoles = selectedRolesTemp.filter((role) => role?.id !== roleId);
      setSelectedRolesTemp(updatedRoles);
    } else {
      setSelectedRolesTemp([...selectedRolesTemp, role]);
    }
  };

  const isMandatoryCustomFieldEmpty = (
    customFieldGroupData: CustomFieldGroupProps[]
  ): boolean => {
    return customFieldGroupData.some((groupData) =>
      groupData.data.some(
        (field) =>
          isEmpty(field.value) &&
          field.mandatoryLevel === MANDATORY_LEVEL.Mandatory
      )
    );
  };

  const isConditionalCustomFieldEmpty = (
    customFieldGroupData: CustomFieldGroupProps[]
  ): boolean => {
    let result = false;
    customFieldGroupData.forEach((groupData) => {
      // conditional field only affect fields inside the same parent group
      const hasEmpty = groupData.data.some(
        (field) =>
          isEmpty(field.value) &&
          field.mandatoryLevel === MANDATORY_LEVEL.Conditional
      );
      const hasValue = groupData.data.some(
        (field) =>
          !isEmpty(field.value) &&
          field.mandatoryLevel === MANDATORY_LEVEL.Conditional
      );
      if (hasEmpty && hasValue) {
        result = true;
        return;
      }
    });
    return result; // as long as one of the conditional fields has data, all the conditional fields cannot be empty
  };

  const renderCodeField = () => {
    if (!editAccess) {
      return (
        <AgSimpleTable
          tableData={[
            { name: messages({ id: 'CODE' }), value: orgData?.clientId ?? '' }
          ]}
          style={{
            paddingTop: '16px'
          }}
        />
      );
    }
    return (
      <AgTextField
        optionalText
        label={messages({ id: 'CODE' })}
        value={orgData?.clientId ?? ''}
        formStyle={{ marginTop: '16px' }}
        onChange={(e) => {
          if (setOrgDataClientId && orgData) {
            setOrgDataClientId(e.target.value);
          }
        }}
      />
    );
  };

  useEffect(() => {
    setHasStaffProfileError &&
      setHasStaffProfileError(
        isMandatoryCustomFieldEmpty(customFieldGroupData) ||
          isConditionalCustomFieldEmpty(customFieldGroupData)
      );
  }, [customFieldGroupData, setHasStaffProfileError]);

  useEffect(() => {
    if (functionStructureList.length) return;
    const fetch = async () => {
      // if there is no structure list, fetch the list again
      try {
        const structure = await dispatch(getStructure());
        if (structure?.data.length) {
          dispatch(setFunctionStructureList(structure.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    fetch();
  }, [dispatch, functionStructureList.length]);

  useEffect(() => {
    if (
      !functionStructureList ||
      !functionStructureList.length ||
      !orgData?.function
    )
      return;
    const typeName = functionStructureList.find((item) => {
      return item.id === orgData?.function;
    });
    if (typeName?.name) {
      setTypeName(typeName.name);
    }
  }, [functionStructureList, orgData?.function]);

  useEffect(() => {
    if (!tagList || !tagList.length) return;
    let customFieldGroupData: CustomFieldGroupProps[] = [];
    let customFieldGroupData2: CustomFieldGroupProps[] = tagList.map((item) => {
      return {
        groupName: item.key ?? '',
        data: [
          {
            name: item.key ?? '',
            value: '',
            mandatoryLevel: item.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
            fieldType: item.fieldType ?? CustomFieldColumnType.None,
            ...(item.maxLength ? { maxLength: item.maxLength } : null)
          }
        ]
      };
    });
    // if (!orgData?.data?.length) { // this line stops populating custom fields without value when editing
    if (tagList.length) {
      tagList
        .filter(
          (ta) =>
            ta.itemType === ApiItemTypes.Organization ||
            ta.itemType === ApiItemTypes.User
        )
        .forEach((item) => {
          const parentField = tagList.find((tag) => tag.id === item.parentId);
          if (
            !parentField ||
            parentField.fieldType !== MEMBER_PARENT_FIELD_TYPE_NUMBER
          )
            return;
          const isparentFieldExist = customFieldGroupData2.find(
            (field) => field.groupName === parentField.key
          );

          const fieldValue = orgData?.data?.find(field => field?.key === item?.key)?.value ?? '';
          if (isparentFieldExist) {
            isparentFieldExist.data.push({
              name: item.key ?? '',
              value: fieldValue,
              mandatoryLevel: item.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
              fieldType: item.fieldType ?? CustomFieldColumnType.None,
              ...(item.maxLength ? { maxLength: item.maxLength } : null)
            });
            customFieldGroupData.push(isparentFieldExist);
          } else {
            customFieldGroupData.push({
              groupName: item.key ?? '',
              data: [
                {
                  name: item.key ?? '',
                  value: fieldValue,
                  mandatoryLevel:
                    item.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
                  fieldType: item.fieldType ?? CustomFieldColumnType.None,
                  ...(item.maxLength ? { maxLength: item.maxLength } : null)
                }
              ]
            });
          }
        });
    }
    // the code below stops populating custom fields without value when editing, 90% sure the code can be deleted, keep it here for days just in case
    // } else {
    //   orgData.data.forEach((field) => {
    //     const customField = tagList.find(
    //       (tag) => tag.key?.toUpperCase() === field.key.toUpperCase()
    //     );
    //     if (!customField) return;
    //     const parentField = tagList.find(
    //       (tag) => tag.id === customField.parentId
    //     );
    //     if (
    //       !parentField ||
    //       parentField.fieldType !== MEMBER_PARENT_FIELD_TYPE_NUMBER
    //     )
    //       return;
    //     const isparentFieldExist = customFieldGroupData.find(
    //       (field) => field.groupName === parentField.key
    //     );
    //
    //     if (isparentFieldExist) {
    //       isparentFieldExist.data.push({
    //         name: customField.key ?? '',
    //         value: field.value ?? '',
    //         mandatoryLevel:
    //           customField.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
    //         fieldType: customField.fieldType ?? CustomFieldColumnType.None,
    //         ...(customField.maxLength
    //           ? { maxLength: customField.maxLength }
    //           : null)
    //       });
    //     } else {
    //       customFieldGroupData.push({
    //         groupName: parentField.key ?? '',
    //         data: [
    //           {
    //             name: customField.key ?? '',
    //             value: field.value ?? '',
    //             mandatoryLevel:
    //               customField.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
    //             fieldType: customField.fieldType ?? CustomFieldColumnType.None,
    //             ...(customField.maxLength
    //               ? { maxLength: customField.maxLength }
    //               : null)
    //           }
    //         ]
    //       });
    //     }
    //   });
    // }

    const uniqueGroupNames = new Set<string>();
    const childNames = new Set<string>();

    const uniqueCustomFieldGroupData = customFieldGroupData.filter((group) => {
      if (!childNames.has(group.groupName)) {
        uniqueGroupNames.add(group.groupName);

        group.data.forEach((field) => {
          if (field.name === group.groupName) {
            childNames.add(field.name);
          }
        });

        return true;
      } else {
        return false;
      }
    });

    uniqueCustomFieldGroupData.map((gn) =>
      gn.data.map((d) => {
        if (d.fieldType === ApiColumnType.Enum) {
          const id = parseInt(d.name);
          if (!id) return;
          const enumx = enumList?.find((en) => en.id === id);
          if (enumx?.name) d.name = enumx.name;
          if (enumx?.values?.length && enumx?.values?.length > 0 && d.value) {
            d.value =
              enumx?.values.find((v) => v.id === parseInt(d.value))?.name || '';
          }
        }
      })
    );

    customFieldGroupData && setCustomFieldGroupData(uniqueCustomFieldGroupData);
  }, [orgData?.data, tagList]);

  const handleUpdateRoles = () => {
    const roleIds = selectedRolesTemp.map((role) => Number(role.id));
    dispatch(setSelectedRoles(roleIds));
    setOpenRoleSelectionModal(false);
  };

  return (
    <AgTypography
      style={{
        padding: '24px 16px',
        display: 'grid',
        // width: 'auto',
        gap: '24px'
      }}
    >
      <AgTypography
        style={{
          display: 'grid',
          gap: '16px'
        }}
      >
        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Bold'}
          message={messages({ id: 'STAFF_WORKS_AT' })}
          style={{
            color: AgColor.grey6
          }}
        />
        
        <AgOrganizationalChips
          type={'selected'}
          label={orgData?.name || ''}
          orgId={orgData?.id?.toString() || ''}
          orgType={
            functionStructureList.find((item) => {
              return item.id === orgData?.function;
            })?.name || ''
          }
          width={'auto'}
        />
      </AgTypography>

      <Divider sx={{ margin: '8px 0', color: AgColor.grey4 }} />

      <AgTypography
        style={{
          display: 'grid',
          gap: '16px'
        }}
      >
        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Bold'}
          message={messages({ id: 'ROLES' })}
          style={{
            color: AgColor.grey6
          }}
        />
        <AgTypography
          style={{
            display: 'flex',
            height: '48px',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          {selectedRolesTemp?.map((role, index) => {
            return (
              <AgRoleChip
                key={index}
                type={'staff'}
                message={role.name || ''}
                popOverTitle={role.name || ''}
                popOverDescription={role.description || ''}
              />
            );
          })}
          
          {
            ((window.location.pathname === ROOT_PATHS.ADD_STAFF) || editAccess) &&
            allRoles.length >= 1 &&
            handleEditRoles()
          }
        </AgTypography>
      </AgTypography>

      {
        isProfile &&
        <>
          <Divider sx={{ margin: '8px 0', color: AgColor.grey4 }} />

          <AgTypography
            style={{
              display: 'grid',
              gap: '16px'
            }}
          >
            <AgTypography
              contentStyle={'AP.H5.UPPERCASE.Bold'}
              message={messages({ id: 'STAFF_DETAILS' })}
              style={{
                color: AgColor.grey6
              }}
            />
            
            <AgTextField
              label={messages({ id: 'BUSINESSTITLE' })}
              onChange={() => {}}
              optionalText
            />
          </AgTypography>
        </>
      }

      {openRoleSelectionModal && (
        <AgModal
          closeIcon
          onCloseModal={() => setOpenRoleSelectionModal(false)}
          open={openRoleSelectionModal}
          title={messages({ id: 'ROLES' })}
          body={
            <AgTypography style={{ marginTop: '16px', marginBottom: '80px' }}>
              <AgTypography
                contentStyle={'AP.Paragraph.Large.Regular'}
                message={messages({ id: 'MEMBER_ROLE_SELECTION' })}
              />
              {allRoles?.map(
                (role) =>
                  role && (
                    <Box display='flex' key={role?.id} alignItems='flex-start' py={2} borderBottom={'1px solid ' + AgColor.grey4}>
                      <Box pt={'7px'}>
                        <AgCheckBox
                          checked={selectedRolesTemp.map(x => x?.id).includes(role?.id)}
                          label={''}
                          onChange={() => handleRoleSelection(role)}
                        />
                      </Box>
                      <Box mx={2}>
                        <AgRoleChip
                          type={'staff'}
                          message={role.name || ''}
                        />
                        <AgTypography
                          contentStyle={'AP.Paragraph.Medium.Regular'}
                          style={{ marginTop: '10px' }}
                          message={role.description || ''}
                        />
                      </Box>
                    </Box>
                  )
              )}
            </AgTypography>
          }
          footer={
            <Fragment>
              <AgButton
                onClick={() => setOpenRoleSelectionModal(false)}
                style={{ marginRight: '32px' }}
                variant={'outlined'}
                label={messages({ id: 'CANCEL' })}
              />
              <AgButton
                onClick={() => handleUpdateRoles()}
                disabled={!selectedRolesTemp.length}
                label={messages({ id: 'UPDATE_ROLES' })}
              />
            </Fragment>
          }
          footerSX={{ boxSizing: 'border-box' }}
        />
      )}
    </AgTypography>
  );
};

export default Index;
